import i18n from "i18next";
import { initReactI18next } from "react-i18next";

/**
 * Comprehensive i18n dictionary for the ExpenseSave app.
 * Locales: English (en), Hindi (hi), Spanish (es), French (fr).
 *
 * Rules:
 *  - Universally understood terms (AI, Email, INR, Scan, Backup, Security)
 *    may be left in English inside Hindi strings for clarity.
 *  - Keep placeholders like {{count}} intact.
 */

const en = {
  // Brand
  app_name: "Expense & Save",
  tagline: "Personal finance, distilled",

  // Auth
  welcome_back: "Welcome back",
  sign_in: "Sign in",
  sign_up: "Sign up",

  // Sidebar nav
  dashboard: "Dashboard",
  expenses: "Expenses",
  income: "Income",
  insights: "Insights",
  recurring: "Recurring",
  goals: "Goals",
  debts: "Debts",
  investments: "Investments",
  workspaces: "Workspaces",
  admin: "Admin",
  budget: "Budget",
  categories: "Categories",
  profile: "Profile",
  logout: "Logout",

  // Greeting
  greet_welcome_back: "Welcome back",
  greet_morning: "Good Morning",
  greet_afternoon: "Good Afternoon",
  greet_evening: "Good Evening",

  // Header / accessibility
  open_menu: "Open menu",
  skip_to_main: "Skip to main content",
  settings: "Settings",

  // Dashboard core
  overview: "Overview",
  balance: "Balance",
  balance_hint: "Income − Expense this month",
  savings: "Savings",
  budget_remaining: "Budget remaining",
  monthly_budget: "Monthly budget",
  no_budget_set: "No budget set",
  of_remaining: "of {{amount}} remaining",
  set_one_in_budget: "Set one in Budget →",
  manage_monthly_budget: "Manage monthly budget",
  set_monthly_budget: "Set monthly budget",
  percent_used: "{{n}}% used",

  // Quick Actions
  quick_actions: "Quick Actions",
  quick_actions_subtitle: "Manage smarter. Save better. One tap away.",
  add_income: "Add Income",
  add_expense: "Add Expense",
  qa_income: "Income",
  qa_expense: "Expense",
  qa_voice: "Voice",
  qa_voice_sub: "Speak & Add",
  qa_scan: "Scan",
  qa_scan_sub: "Scan Receipt",
  qa_ai: "AI Assistant",
  qa_ai_sub: "Personal Finance Advisor",

  // AI Advisor card
  ai_advisor: "AI Advisor",
  ai_live: "LIVE",
  ai_title: "Understand. Save. Grow.",
  ai_desc: "Get clear insights and smart tips to manage your money better, and Ask AI Anytime.",
  ai_open_advisor: "Ask Advisor",

  // Bill reminders
  coming_up: "Coming up",
  bills_due_this_week: "Bills due this week",
  view_all: "View all",
  due_overdue: "Overdue",
  due_today: "Today",
  due_tomorrow: "Tomorrow",
  due_in_days: "in {{n}} days",
  subscription: "subscription",

  // Period summary
  period_summary: "Period summary",
  period_summary_sub: "A glance at the recent activity",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  saved: "Saved",

  // Charts
  expense_by_category: "Expense by category",
  this_month: "This month",
  empty_chart_expense: "Add expenses to see a breakdown",
  income_vs_expense: "Income vs Expense",
  last_6_months: "Last 6 months",
  empty_chart_compare: "Track a few months to compare flows",
  savings_trend: "Savings trend",
  empty_chart_savings: "Savings will appear as you log income and expense",

  // Recent transactions
  recent_transactions: "Recent transactions",
  latest_activity: "Latest activity",
  empty_transactions: "Nothing here yet. Add your first transaction to begin.",
  th_date: "Date",
  th_category_source: "Category / Source",
  th_notes: "Notes",
  th_amount: "Amount",

  // Backup
  bill_reminders: "Bills coming up",
  backup_export: "Export backup",
  backup_restore: "Restore backup",

  // Settings
  language: "Language",

  // Profile page
  profile_settings: "Profile & Settings",
  tab_details: "Details",
  tab_backup: "Backup",
  tab_security: "Security",
  field_name: "Name",
  field_email: "Email",
  field_currency: "Currency",
  notification_preferences: "Notification preferences",
  email_summaries: "Email summaries",
  email_summaries_hint: "Weekly digest of income, expense and savings.",
  budget_alerts: "Budget alerts",
  budget_alerts_hint: "Heads-up when you're crossing 80% of your monthly budget.",
  save_changes: "Save changes",
  saving: "Saving…",
  backup_restore_title: "Backup & restore",
  backup_restore_hint: "Download a JSON snapshot of your account, or merge-restore from a previous backup. Restores never overwrite existing data.",
  download_backup: "Download backup",
  restoring: "Restoring…",
  restore_from_json: "Restore from JSON",
  encrypted_backup: "Encrypted backup",
  encrypted_backup_hint: "AES-256-GCM with PBKDF2-derived key. Use the same password to decrypt — there is no recovery if you forget it.",
  password_8_plus: "Password (8+ chars)",
  choose_passphrase: "Choose a strong passphrase",
  download_encrypted: "Download encrypted",
  restore_encrypted: "Restore encrypted",
  push_notifications: "Push notifications",
  push_hint: "Get bill reminders and budget alerts on your device, even when the app is closed.",
  push_unsupported: "Your browser doesn't support push notifications.",
  enabled: "Enabled",
  disabled: "Disabled",
  push_blocked: "Browser blocked notifications — unblock in site settings.",
  push_toggle_hint: "Toggle to opt-in or out.",
  send_test: "Send test",
  change_password: "Change password",
  current_password: "Current password",
  new_password: "New password",
  updating: "Updating…",
  update_password: "Update password",

  // Danger zone / Delete account
  danger_zone: "Danger Zone",
  delete_account: "Delete Account",
  delete_account_desc: "Permanently delete your account and associated personal data.",
  delete_account_title: "Delete your account?",
  delete_account_message: "This action is permanent and cannot be undone.",
  delete_account_confirm: "Permanently Delete Account",
  cancel: "Cancel",
  delete_account_password_prompt: "Enter your current password to confirm",
  deleting: "Deleting…",

  // Upgrade legacy (kept for backward compatibility)
  upgrade_to_pro: "Upgrade to Pro",
  pro: "Pro",
  free_trial: "Free trial",
};

const hi = {
  // Brand
  app_name: "Expense & Save",
  tagline: "पर्सनल फ़ाइनेंस, आसान भाषा में",

  // Auth
  welcome_back: "वापस स्वागत है",
  sign_in: "साइन इन",
  sign_up: "साइन अप",

  // Sidebar nav
  dashboard: "डैशबोर्ड",
  expenses: "खर्च",
  income: "आय",
  insights: "इनसाइट्स",
  recurring: "रिकरिंग",
  goals: "लक्ष्य",
  debts: "कर्ज़",
  investments: "निवेश",
  workspaces: "वर्कस्पेस",
  admin: "एडमिन",
  budget: "बजट",
  categories: "श्रेणियां",
  profile: "प्रोफ़ाइल",
  logout: "लॉगआउट",

  // Greeting
  greet_welcome_back: "वापस स्वागत है",
  greet_morning: "सुप्रभात",
  greet_afternoon: "नमस्कार",
  greet_evening: "शुभ संध्या",

  // Header / accessibility
  open_menu: "मेन्यू खोलें",
  skip_to_main: "मुख्य सामग्री पर जाएँ",
  settings: "सेटिंग्स",

  // Dashboard core
  overview: "ओवरव्यू",
  balance: "बैलेंस",
  balance_hint: "इस महीने की आय − खर्च",
  savings: "बचत",
  budget_remaining: "बचा हुआ बजट",
  monthly_budget: "मासिक बजट",
  no_budget_set: "कोई बजट सेट नहीं",
  of_remaining: "{{amount}} में से बाकी",
  set_one_in_budget: "बजट में सेट करें →",
  manage_monthly_budget: "मासिक बजट मैनेज करें",
  set_monthly_budget: "मासिक बजट सेट करें",
  percent_used: "{{n}}% इस्तेमाल",

  // Quick Actions
  quick_actions: "त्वरित क्रियाएं",
  quick_actions_subtitle: "स्मार्ट तरीके से मैनेज करें। बेहतर बचत करें। बस एक टैप दूर।",
  add_income: "आय जोड़ें",
  add_expense: "खर्च जोड़ें",
  qa_income: "आय",
  qa_expense: "खर्च",
  qa_voice: "वॉइस",
  qa_voice_sub: "बोलें और जोड़ें",
  qa_scan: "स्कैन",
  qa_scan_sub: "रसीद स्कैन करें",
  qa_ai: "AI असिस्टेंट",
  qa_ai_sub: "पर्सनल फ़ाइनेंस एडवाइज़र",

  // AI Advisor card
  ai_advisor: "AI एडवाइज़र",
  ai_live: "LIVE",
  ai_title: "समझें। बचाएं। बढ़ाएं।",
  ai_desc: "अपने पैसे को बेहतर मैनेज करने के लिए साफ़ इनसाइट्स और स्मार्ट टिप्स पाएं, और AI से कभी भी पूछें।",
  ai_open_advisor: "एडवाइज़र से पूछें",

  // Bill reminders
  coming_up: "आने वाला",
  bills_due_this_week: "इस हफ़्ते के बिल",
  view_all: "सभी देखें",
  due_overdue: "देय तिथि निकल गई",
  due_today: "आज",
  due_tomorrow: "कल",
  due_in_days: "{{n}} दिनों में",
  subscription: "सब्सक्रिप्शन",

  // Period summary
  period_summary: "अवधि सारांश",
  period_summary_sub: "हाल की गतिविधि पर एक नज़र",
  daily: "रोज़",
  weekly: "साप्ताहिक",
  monthly: "मासिक",
  saved: "बचाया",

  // Charts
  expense_by_category: "श्रेणी के अनुसार खर्च",
  this_month: "इस महीने",
  empty_chart_expense: "ब्रेकडाउन देखने के लिए खर्च जोड़ें",
  income_vs_expense: "आय बनाम खर्च",
  last_6_months: "पिछले 6 महीने",
  empty_chart_compare: "तुलना के लिए कुछ महीने ट्रैक करें",
  savings_trend: "बचत ट्रेंड",
  empty_chart_savings: "जैसे-जैसे आप आय और खर्च दर्ज करेंगे, बचत यहाँ दिखाई देगी",

  // Recent transactions
  recent_transactions: "हाल के लेन-देन",
  latest_activity: "नवीनतम गतिविधि",
  empty_transactions: "अभी यहाँ कुछ नहीं है। शुरू करने के लिए अपना पहला लेन-देन जोड़ें।",
  th_date: "तारीख़",
  th_category_source: "श्रेणी / स्रोत",
  th_notes: "नोट्स",
  th_amount: "राशि",

  // Backup
  bill_reminders: "आने वाले बिल",
  backup_export: "बैकअप एक्सपोर्ट",
  backup_restore: "बैकअप रीस्टोर",

  // Settings
  language: "भाषा",

  // Profile page
  profile_settings: "प्रोफ़ाइल और सेटिंग्स",
  tab_details: "विवरण",
  tab_backup: "बैकअप",
  tab_security: "सुरक्षा",
  field_name: "नाम",
  field_email: "ईमेल",
  field_currency: "मुद्रा",
  notification_preferences: "नोटिफ़िकेशन प्राथमिकताएं",
  email_summaries: "ईमेल सारांश",
  email_summaries_hint: "आय, खर्च और बचत का साप्ताहिक डाइजेस्ट।",
  budget_alerts: "बजट अलर्ट",
  budget_alerts_hint: "जब आप अपने मासिक बजट का 80% पार करेंगे तब सूचना।",
  save_changes: "बदलाव सहेजें",
  saving: "सहेजा जा रहा है…",
  backup_restore_title: "बैकअप और रीस्टोर",
  backup_restore_hint: "अपने अकाउंट का JSON स्नैपशॉट डाउनलोड करें, या पिछले बैकअप से मर्ज-रीस्टोर करें। रीस्टोर कभी मौजूदा डेटा को ओवरराइट नहीं करता।",
  download_backup: "बैकअप डाउनलोड करें",
  restoring: "रीस्टोर हो रहा है…",
  restore_from_json: "JSON से रीस्टोर करें",
  encrypted_backup: "एन्क्रिप्टेड बैकअप",
  encrypted_backup_hint: "AES-256-GCM PBKDF2 कुंजी के साथ। डिक्रिप्ट करने के लिए वही पासवर्ड उपयोग करें — भूल जाने पर रिकवरी संभव नहीं।",
  password_8_plus: "पासवर्ड (8+ अक्षर)",
  choose_passphrase: "एक मजबूत पासवर्ड चुनें",
  download_encrypted: "एन्क्रिप्टेड डाउनलोड करें",
  restore_encrypted: "एन्क्रिप्टेड रीस्टोर करें",
  push_notifications: "पुश नोटिफ़िकेशन",
  push_hint: "बिल रिमाइंडर और बजट अलर्ट अपने डिवाइस पर पाएं, ऐप बंद होने पर भी।",
  push_unsupported: "आपका ब्राउज़र पुश नोटिफ़िकेशन सपोर्ट नहीं करता।",
  enabled: "चालू",
  disabled: "बंद",
  push_blocked: "ब्राउज़र ने नोटिफ़िकेशन ब्लॉक किए हैं — साइट सेटिंग्स में अनब्लॉक करें।",
  push_toggle_hint: "चालू/बंद करने के लिए टॉगल करें।",
  send_test: "टेस्ट भेजें",
  change_password: "पासवर्ड बदलें",
  current_password: "मौजूदा पासवर्ड",
  new_password: "नया पासवर्ड",
  updating: "अपडेट हो रहा है…",
  update_password: "पासवर्ड अपडेट करें",

  // Danger zone / Delete account
  danger_zone: "डेंजर ज़ोन",
  delete_account: "खाता हटाएं",
  delete_account_desc: "अपना खाता और संबंधित निजी डेटा स्थायी रूप से हटा दें।",
  delete_account_title: "क्या आप अपना खाता हटाना चाहते हैं?",
  delete_account_message: "यह क्रिया स्थायी है और इसे वापस नहीं किया जा सकता।",
  delete_account_confirm: "खाता स्थायी रूप से हटाएं",
  cancel: "रद्द करें",
  delete_account_password_prompt: "पुष्टि के लिए अपना मौजूदा पासवर्ड डालें",
  deleting: "हटा रहे हैं…",

  // Upgrade legacy
  upgrade_to_pro: "Pro में अपग्रेड करें",
  pro: "Pro",
  free_trial: "फ्री ट्रायल",
};

const es = {
  app_name: "Gasto y Ahorro",
  tagline: "Finanzas personales, claras",
  welcome_back: "Bienvenido de nuevo",
  sign_in: "Iniciar sesión",
  sign_up: "Registrarse",
  dashboard: "Panel",
  expenses: "Gastos",
  income: "Ingresos",
  insights: "Análisis",
  recurring: "Recurrentes",
  goals: "Metas",
  debts: "Deudas",
  investments: "Inversiones",
  workspaces: "Espacios",
  admin: "Administración",
  budget: "Presupuesto",
  categories: "Categorías",
  profile: "Perfil",
  logout: "Cerrar sesión",
  greet_welcome_back: "Bienvenido de nuevo",
  greet_morning: "Buenos días",
  greet_afternoon: "Buenas tardes",
  greet_evening: "Buenas noches",
  open_menu: "Abrir menú",
  skip_to_main: "Ir al contenido",
  settings: "Ajustes",
  overview: "Resumen",
  balance: "Saldo",
  balance_hint: "Ingresos − Gastos este mes",
  savings: "Ahorros",
  budget_remaining: "Presupuesto restante",
  monthly_budget: "Presupuesto mensual",
  no_budget_set: "Sin presupuesto",
  of_remaining: "de {{amount}} restante",
  set_one_in_budget: "Configura uno →",
  manage_monthly_budget: "Gestionar presupuesto",
  set_monthly_budget: "Configurar presupuesto",
  percent_used: "{{n}}% usado",
  quick_actions: "Acciones rápidas",
  quick_actions_subtitle: "Gestiona mejor. Ahorra más. A un toque.",
  add_income: "Agregar ingreso",
  add_expense: "Agregar gasto",
  qa_income: "Ingresos",
  qa_expense: "Gastos",
  qa_voice: "Voz",
  qa_voice_sub: "Hablar y añadir",
  qa_scan: "Escanear",
  qa_scan_sub: "Escanear recibo",
  qa_ai: "AI Asistente",
  qa_ai_sub: "Asesor personal",
  ai_advisor: "AI Asesor",
  ai_live: "LIVE",
  ai_title: "Comprende. Ahorra. Crece.",
  ai_desc: "Obtén información clara y consejos inteligentes, y pregunta a la AI cuando quieras.",
  ai_open_advisor: "Preguntar al asesor",
  coming_up: "Próximamente",
  bills_due_this_week: "Facturas de la semana",
  view_all: "Ver todo",
  due_overdue: "Vencida",
  due_today: "Hoy",
  due_tomorrow: "Mañana",
  due_in_days: "en {{n}} días",
  subscription: "suscripción",
  period_summary: "Resumen del período",
  period_summary_sub: "Un vistazo a la actividad reciente",
  daily: "Diario",
  weekly: "Semanal",
  monthly: "Mensual",
  saved: "Ahorrado",
  expense_by_category: "Gastos por categoría",
  this_month: "Este mes",
  empty_chart_expense: "Agrega gastos para ver el desglose",
  income_vs_expense: "Ingresos vs Gastos",
  last_6_months: "Últimos 6 meses",
  empty_chart_compare: "Registra unos meses para comparar",
  savings_trend: "Tendencia de ahorros",
  empty_chart_savings: "Los ahorros aparecerán al registrar movimientos",
  recent_transactions: "Movimientos recientes",
  latest_activity: "Actividad reciente",
  empty_transactions: "Nada aquí todavía. Agrega tu primer movimiento.",
  th_date: "Fecha",
  th_category_source: "Categoría / Origen",
  th_notes: "Notas",
  th_amount: "Cantidad",
  bill_reminders: "Próximos pagos",
  backup_export: "Exportar copia",
  backup_restore: "Restaurar copia",
  language: "Idioma",
  upgrade_to_pro: "Mejorar a Pro",
  pro: "Pro",
  free_trial: "Prueba gratis",
};

const fr = {
  app_name: "Dépense et Épargne",
  tagline: "Vos finances, en clair",
  welcome_back: "Bon retour",
  sign_in: "Se connecter",
  sign_up: "S'inscrire",
  dashboard: "Tableau de bord",
  expenses: "Dépenses",
  income: "Revenus",
  insights: "Analyses",
  recurring: "Récurrents",
  goals: "Objectifs",
  debts: "Dettes",
  investments: "Investissements",
  workspaces: "Espaces",
  admin: "Administration",
  budget: "Budget",
  categories: "Catégories",
  profile: "Profil",
  logout: "Déconnexion",
  greet_welcome_back: "Bon retour",
  greet_morning: "Bonjour",
  greet_afternoon: "Bon après-midi",
  greet_evening: "Bonsoir",
  open_menu: "Ouvrir le menu",
  skip_to_main: "Aller au contenu principal",
  settings: "Paramètres",
  overview: "Aperçu",
  balance: "Solde",
  balance_hint: "Revenus − Dépenses ce mois",
  savings: "Épargne",
  budget_remaining: "Budget restant",
  monthly_budget: "Budget mensuel",
  no_budget_set: "Aucun budget défini",
  of_remaining: "sur {{amount}} restant",
  set_one_in_budget: "Définir dans Budget →",
  manage_monthly_budget: "Gérer le budget mensuel",
  set_monthly_budget: "Définir le budget mensuel",
  percent_used: "{{n}}% utilisé",
  quick_actions: "Actions rapides",
  quick_actions_subtitle: "Gérez malin. Épargnez mieux. En un geste.",
  add_income: "Ajouter un revenu",
  add_expense: "Ajouter une dépense",
  qa_income: "Revenus",
  qa_expense: "Dépenses",
  qa_voice: "Voix",
  qa_voice_sub: "Parler & ajouter",
  qa_scan: "Scanner",
  qa_scan_sub: "Scanner le reçu",
  qa_ai: "AI Assistant",
  qa_ai_sub: "Conseiller financier",
  ai_advisor: "AI Conseiller",
  ai_live: "LIVE",
  ai_title: "Comprendre. Économiser. Grandir.",
  ai_desc: "Obtenez des analyses claires et des conseils malins, et demandez à l'AI à tout moment.",
  ai_open_advisor: "Poser une question",
  coming_up: "À venir",
  bills_due_this_week: "Factures de la semaine",
  view_all: "Tout voir",
  due_overdue: "En retard",
  due_today: "Aujourd'hui",
  due_tomorrow: "Demain",
  due_in_days: "dans {{n}} jours",
  subscription: "abonnement",
  period_summary: "Résumé de la période",
  period_summary_sub: "Un aperçu de l'activité récente",
  daily: "Quotidien",
  weekly: "Hebdo",
  monthly: "Mensuel",
  saved: "Épargné",
  expense_by_category: "Dépenses par catégorie",
  this_month: "Ce mois",
  empty_chart_expense: "Ajoutez des dépenses pour voir la répartition",
  income_vs_expense: "Revenus vs Dépenses",
  last_6_months: "6 derniers mois",
  empty_chart_compare: "Enregistrez quelques mois pour comparer",
  savings_trend: "Tendance d'épargne",
  empty_chart_savings: "L'épargne apparaîtra dès que vous enregistrerez des flux",
  recent_transactions: "Transactions récentes",
  latest_activity: "Activité récente",
  empty_transactions: "Rien ici pour l'instant. Ajoutez votre première transaction.",
  th_date: "Date",
  th_category_source: "Catégorie / Source",
  th_notes: "Notes",
  th_amount: "Montant",
  bill_reminders: "Factures à venir",
  backup_export: "Exporter la sauvegarde",
  backup_restore: "Restaurer une sauvegarde",
  language: "Langue",
  upgrade_to_pro: "Passer à Pro",
  pro: "Pro",
  free_trial: "Essai gratuit",
};

const resources = {
  en: { translation: en },
  hi: { translation: hi },
  es: { translation: es },
  fr: { translation: fr },
};

const stored = (typeof window !== "undefined" && localStorage.getItem("es_locale")) || "en";

i18n.use(initReactI18next).init({
  resources,
  lng: stored,
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

export const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
];

export function setLanguage(code) {
  if (!resources[code]) return;
  i18n.changeLanguage(code);
  if (typeof window !== "undefined") localStorage.setItem("es_locale", code);
}

export default i18n;
